import type { ITransactionManager } from '@app/domain/ports';
import type { SqliteProvider } from '../providers/sqlite.provider';

/**
 * SQLite-backed implementation of ITransactionManager.
 * Wraps any unit of work in a BEGIN / COMMIT / ROLLBACK cycle.
 *
 * SQLite allows only one active transaction at a time on a single connection.
 * Concurrent `run()` calls would fail with "cannot start a transaction within
 * a transaction", and the resulting ROLLBACK from the failed caller would
 * silently end the first caller's transaction — producing "cannot commit"
 * when it finishes. The queue below serializes callers so each waits for the
 * previous transaction to complete before starting its own BEGIN.
 */
export class SqliteTransactionManager implements ITransactionManager {
  /**
   * A chain of promises that serializes concurrent run() calls.
   * Each new call appends itself to the tail and waits for the previous entry.
   */
  private queue: Promise<void> = Promise.resolve();

  constructor(private readonly provider: SqliteProvider) {}

  async run<T>(work: () => Promise<T>): Promise<T> {
    // Capture the current tail so this call can wait behind it.
    const waitFor = this.queue;

    // Replace the shared tail with a promise that resolves when THIS call
    // finishes (success or failure). Never rejects — callers don't need to
    // handle each other's errors, only their own.
    let release!: () => void;
    this.queue = new Promise<void>((resolve) => {
      release = resolve;
    });

    // Wait for all previously queued transactions to complete.
    await waitFor;

    await this.provider.beginTransaction();
    try {
      const result = await work();
      await this.provider.commit();
      return result;
    } catch (error) {
      try {
        await this.provider.rollback();
      } catch (rollbackError) {
        // The transaction may have already been ended automatically by SQLite
        // (e.g. SQLITE_IOERR, SQLITE_FULL, or certain WASM worker error paths).
        // Log the rollback failure but always rethrow the original error so the
        // caller sees the root cause, not the secondary "no transaction active" noise.
        console.warn('[SqliteTransactionManager] rollback failed (transaction may already be closed):', rollbackError);
      }
      throw error;
    } finally {
      // Unblock the next queued transaction regardless of outcome.
      release();
    }
  }
}
